import React, { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import './Note.css';

// Move calculations outside component for better performance
// Create a deterministic but seemingly random number between -1.5 and 1.5
const hashCode = str => 
  str.split('').reduce((a, b) => (((a << 5) - a) + b.charCodeAt(0))|0, 0);

// Memoized name part extraction function
const getNameParts = (name) => {
  const parts = name.split(' ');
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' ')
  };
};

// Generate a deterministic rotation based on name
const getRandomRotation = (name) => {
  const nameHash = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  // Make more extreme rotations less common by using a non-linear distribution
  return ((nameHash % 160) / 10) - 8;
};

// Generate small random offsets for more natural positioning
const getPositionOffset = (name) => {
  const nameHash = hashCode(name);
  // Use different parts of the hash for x and y to ensure they're different
  // Increased the range for more natural spread (from ±1.5 to ±2)
  const xOffset = ((nameHash & 0xFF) / 255 * 4) - 2;
  const yOffset = (((nameHash >> 8) & 0xFF) / 255 * 4) - 2;

  return { x: xOffset, y: yOffset };
};

// Generate unique wind animation timing per note for organic movement
const getWindTiming = (name) => {
  const nameHash = hashCode(name);
  // Duration between 15-25 seconds
  const duration = 15 + ((nameHash & 0xFF) / 255 * 10);
  // Delay between 0-10 seconds to stagger animations
  const delay = ((nameHash >> 8) & 0xFF) / 255 * 10;

  return { duration, delay };
};

/**
 * Note component to display individual memorial notes on the wall
 * @param {Object} props - Component props
 * @param {string} props.name - Full name of the person being memorialized
 * @param {Object} props.position - Position coordinates {x, y} in percentage
 * @param {Function} props.onClick - Click handler function
 * @param {boolean} props.isMobile - Whether the note should render in mobile mode
 * @returns {JSX.Element} Rendered Note component
 */
const Note = ({ name, position, onClick, isMobile, viewed = false, hasCandle = false, hasStone = false }) => {
  // Touch feedback state
  const [ripple, setRipple] = useState(false);
  
  // Split name into first and last parts - memoized
  const nameParts = useMemo(() => getNameParts(name), [name]);

  // Generate a deterministic rotation - memoized
  const randomRotation = useMemo(() => getRandomRotation(name), [name]);

  // Get consistent position offsets - memoized
  const offset = useMemo(() => getPositionOffset(name), [name]);

  // Get unique wind animation timing - memoized
  const windTiming = useMemo(() => getWindTiming(name), [name]);

  // Handle touch start for mobile ripple effect
  const handleTouchStart = useCallback(() => {
    setRipple(true);
    // Reset ripple state after animation completes
    setTimeout(() => setRipple(false), 400);
  }, []);
  
  // Handle keyboard interaction for accessibility
  const handleKeyDown = useCallback((e) => {
    // Activate on Enter or Space
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault(); // Prevent page scroll on space
      onClick();
    }
  }, [onClick]);

  return (
    <div
      className={`note ${isMobile ? 'mobile' : ''} ${viewed ? 'viewed' : ''} ${ripple ? 'ripple-effect' : ''} ${hasCandle ? 'has-candle' : ''} ${hasStone ? 'has-stone' : ''}`}
      style={{
        left: `${position.x + offset.x}%`,
        top: `${position.y + offset.y}%`,
        cursor: 'pointer',
        // Set CSS variables for rotation and wind animation
        '--note-rotation': `${randomRotation}deg`,
        '--wind-duration': `${windTiming.duration}s`,
        '--wind-delay': `${windTiming.delay}s`,
      }}
      onClick={onClick}
      onTouchStart={handleTouchStart}
      onKeyDown={handleKeyDown}
      aria-label={`Memorial note for ${name}${viewed ? ' (viewed)' : ''}${hasCandle ? ' (candle lit)' : ''}${hasStone ? ' (stone placed)' : ''}`}
      role="button"
      tabIndex={0}
      data-testid={`note-${name.replace(/\s+/g, '-').toLowerCase()}`}
      data-viewed={viewed}
      data-rotation={randomRotation}
      data-candle={hasCandle}
      data-stone={hasStone}
    >
      <div className="note-content">
        <div className="firstName">{nameParts.firstName}</div>
        <div className="lastName">{nameParts.lastName}</div>
      </div>
      {viewed && <span className="viewed-indicator" aria-hidden="true" />}
      {hasCandle && (
        <span className="candle-indicator" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="miniFlameGrad" x1="50%" y1="100%" x2="50%" y2="0%">
                <stop offset="0%" stopColor="#ff9500" />
                <stop offset="50%" stopColor="#ffcc00" />
                <stop offset="100%" stopColor="#ffffee" />
              </linearGradient>
            </defs>
            <path
              d="M12 4C12 4 9 7 9 10C9 11.5 10 13 12 13C14 13 15 11.5 15 10C15 7 12 4 12 4Z"
              fill="url(#miniFlameGrad)"
            />
            <rect x="11" y="13" width="2" height="6" rx="0.5" fill="#e8dcc8" />
          </svg>
        </span>
      )}
      {hasStone && (
        <span className="stone-indicator" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="12" cy="12" rx="6" ry="4" fill="#8b7355" stroke="#5b4636" strokeWidth="0.5" />
            <ellipse cx="10" cy="11" rx="2" ry="1" fill="rgba(255, 255, 255, 0.3)" />
          </svg>
        </span>
      )}
      <div className="ripple-container" aria-hidden="true"></div>
      <div className="note-tape" aria-hidden="true"></div>
    </div>
  );
};

Note.propTypes = {
  name: PropTypes.string.isRequired,
  position: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  viewed: PropTypes.bool,
  hasCandle: PropTypes.bool,
  hasStone: PropTypes.bool,
};

// Using default parameters instead of defaultProps
const NoteWithDefaults = ({ isMobile = false, hasCandle = false, hasStone = false, ...props }) => (
  <Note isMobile={isMobile} hasCandle={hasCandle} hasStone={hasStone} {...props} />
);

NoteWithDefaults.propTypes = Note.propTypes;

export default React.memo(NoteWithDefaults);